import { useMemo } from 'react';
import { ComponentHealthWrapFn, createComponentHealth } from '@shared/components/ComponentHealth';
import { useHistory } from '@shared/core';
import { withWindow } from '@shared/utils/withWindow';
import PageNotFound from '@apps/pageNotFound/PageNotFound';

const COMPONENT_SLOW_LOG_TIMEOUT_MS = 5_000;

const [wrapWithHotelBookingRedirectProviderRoot, useHotelBookingRedirectHealthRoot] = createComponentHealth('HotelBookingRedirect');

export const wrapWithHotelBookingRedirectProvider: ComponentHealthWrapFn = <T extends {}>(Component: React.ComponentType<T>) => {
  const wrappedWithRoot = wrapWithHotelBookingRedirectProviderRoot(Component);
  return (props: T) => wrappedWithRoot({ slowLogTimeout: COMPONENT_SLOW_LOG_TIMEOUT_MS, forceErrorUIOnTimeout: true, errorComponent: PageNotFound, ...props });
};

type ComponentHealthOptions = {
  url?: string | URL;
  isExternalUrl?: boolean;
  forceErrorUI?: boolean;
};

export interface ComponentHealthCallbacksWithRedirect {
  onLoadComplete: (options?: ComponentHealthOptions) => void;
  onLoadFailure: (reason: unknown, context?: object, options?: ComponentHealthOptions) => void;
}

export const useHotelBookingRedirectHealth = (): ComponentHealthCallbacksWithRedirect => {
  const history = useHistory();
  const { onLoadComplete: baseComplete, onLoadFailure: baseFailure } = useHotelBookingRedirectHealthRoot();

  const enhancedCallbacks = useMemo<ComponentHealthCallbacksWithRedirect>(() => {
    const handleRedirect = (options?: ComponentHealthOptions) => {
      if (!options?.url) return;

      const redirectUrl = options.url instanceof URL ? options.url.toString() : options.url;

      const isExternal = options.isExternalUrl ?? redirectUrl.startsWith('http');

      if (isExternal) {
        // For full URLs, navigate using window.location
        withWindow(global => global.location.replace(redirectUrl));
      } else {
        // For internal paths, use SPA navigation
        history.push(redirectUrl);
      }
    };

    return {
      onLoadComplete: (options?: ComponentHealthOptions) => {
        baseComplete();
        handleRedirect(options);
      },
      onLoadFailure: (reason, context, options?: ComponentHealthOptions) => {
        baseFailure(reason, context, options?.forceErrorUI);
        handleRedirect(options);
      }
    };
  }, [baseComplete, baseFailure, history]);

  return enhancedCallbacks;
};
